"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getValidMonitorAggregationsForMeasure = void 0;
exports.isValidQuery = isValidQuery;
const dataModel_1 = require("../query/dataModel");
const types_1 = require("../query/types");
const filterColumns_1 = require("./filterColumns");
/** getValidMonitorAggregationsForMeasure returns the aggregations valid for a monitor metric: the widget set minus `histogram`, and pinned to the measure's inner `aggs.agg` when set (e.g. observations.count is always `count`). */
const getValidMonitorAggregationsForMeasure = (measure) => {
    const aggs = (0, types_1.getValidAggregationsForMeasureType)(measure?.type).filter((a) => a !== "histogram");
    const pinned = measure?.aggs?.agg;
    if (pinned && pinned !== "histogram" && aggs.includes(pinned)) {
        return [pinned];
    }
    return aggs;
};
exports.getValidMonitorAggregationsForMeasure = getValidMonitorAggregationsForMeasure;
/**
 * isValidQuery partitions a monitor's metrics against the view's v2
 * declaration. A bad filter is a whole-query failure (every metric rejected);
 * otherwise each metric is partitioned by the measure-exists + aggregation
 * checks, `reason` carrying the first rejection. Filter-column-vs-view
 * alignment is intentionally NOT checked here (see LF-2181) — dimensions are
 * the group-by surface, not the filter surface, so the dimension map is the
 * wrong proxy.
 */
function isValidQuery(input) {
    const declaration = (0, dataModel_1.getViewDeclaration)(input.view, "v2");
    const filterReason = invalidFilterReason(declaration, input.filters);
    if (filterReason) {
        return {
            valid: false,
            reason: filterReason,
            accepted: [],
            rejected: input.metrics,
        };
    }
    const accepted = [];
    const rejected = [];
    let reason;
    for (const m of input.metrics) {
        const metricReason = invalidMetricReason(declaration, input.view, m);
        if (metricReason) {
            rejected.push(m);
            if (reason === undefined) {
                reason = metricReason;
            }
        }
        else {
            accepted.push(m);
        }
    }
    if (reason)
        return { valid: false, reason, accepted, rejected };
    return { valid: true, accepted, rejected: [] };
}
/** invalidFilterReason returns why a filter makes the whole query invalid, or undefined when all filters pass. */
function invalidFilterReason(declaration, filters) {
    for (const filter of filters) {
        if (filterColumns_1.disallowedMonitorFilterColumns.includes(filter.column)) {
            return (`Filter on "${filter.column}" is not supported for monitors — ` +
                `too expensive at evaluation cadence.`);
        }
        // Set-semantics value arrays must not contain duplicates — `any of` /
        // `none of` / `all of` are set operators, so duplicate elements produce
        // a logically identical filter that would fragment schedulerBatchId.
        if (filter.type === "stringOptions" ||
            filter.type === "categoryOptions" ||
            filter.type === "arrayOptions") {
            if (new Set(filter.value).size !== filter.value.length) {
                return (`Filter on "${filter.column}" (type "${filter.type}") must have unique values; ` +
                    `duplicates are not allowed for set-semantics operators.`);
            }
        }
    }
    return undefined;
}
/** invalidMetricReason returns why a metric doesn't resolve on the view declaration, or undefined when it is valid. */
function invalidMetricReason(declaration, view, m) {
    if (!Object.hasOwn(declaration.measures, m.measure)) {
        return (`Invalid measure "${m.measure}" for view "${view}". ` +
            `Must be one of: ${Object.keys(declaration.measures).join(", ")}`);
    }
    const measureDef = declaration.measures[m.measure];
    if (m.aggregation === "histogram") {
        return (`Aggregation "histogram" is not supported for monitors — it produces ` +
            `a bucket array, not a scalar value comparable to the threshold.`);
    }
    const validAggs = (0, exports.getValidMonitorAggregationsForMeasure)(measureDef);
    if (!validAggs.some((a) => a === m.aggregation)) {
        return (`Aggregation "${m.aggregation}" is not valid for measure ` +
            `"${m.measure}" (type: ${measureDef.type}). Valid: ${validAggs.join(", ")}`);
    }
    return undefined;
}
